import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Backpack, Bike, Briefcase, Car, Luggage, Package, Tent, Truck } from "lucide-react";
import { usePlatform } from "@/context/PlatformContext";

/**
 * Animated asset map for the Bundle selector.
 * As the shopper flips between 1 / 2 / 4 / 8-Pack, more pins drop onto
 * the map — showing exactly which things they'd be tracking. The pins
 * are stable across pack changes (we only add, never reshuffle).
 *
 * The matching phone mockup lives in a sibling column and shares the
 * `selectedId` state via props so tapping on the phone pulses the pin
 * and tapping on a pin highlights the phone row.
 */

export const BUNDLE_ASSETS = [
  { id: "car",      icon: Car,       label: "Car",       place: "Driveway",  tip: "Inside the wheel arch or boot frame.", x: 15, y: 68 },
  { id: "trailer",  icon: Truck,     label: "Trailer",   place: "Backyard",  tip: "On the drawbar or chassis rail.",       x: 32, y: 44 },
  { id: "toolbox",  icon: Briefcase, label: "Toolbox",   place: "Workshop",  tip: "Inside the lid, out of sight.",         x: 50, y: 72 },
  { id: "bike",     icon: Bike,      label: "Bike",      place: "Garage",    tip: "Frame tube or under the saddle.",       x: 68, y: 50 },
  { id: "luggage",  icon: Luggage,   label: "Luggage",   place: "Hallway",   tip: "Zip it into an inner pocket.",          x: 86, y: 30 },
  { id: "camping",  icon: Tent,      label: "Camping",   place: "Garden",    tip: "On the tent-pole bag or roof box.",     x: 24, y: 22 },
  { id: "backpack", icon: Backpack,  label: "Backpack",  place: "Office",    tip: "On the steel buckle or inner tag.",     x: 56, y: 24 },
  { id: "gear",     icon: Package,   label: "Equipment", place: "Job site",  tip: "On the steel frame of any kit.",        x: 72, y: 68 },
];

export const visibleAssetsFor = (packId) => {
  const n = packId >= 8 ? 8 : packId >= 4 ? 4 : packId >= 2 ? 2 : 1;
  return BUNDLE_ASSETS.slice(0, n);
};

/** Smooth cubic-ish path through every visible pin using midpoint smoothing.
 *  No zig-zag, no sharp corners — the route reads as one continuous travel line. */
const buildRoutePath = (pts) => {
  if (!pts.length) return "";
  const sx = (p) => p.x * 1.6;
  const sy = (p) => p.y * 0.9;
  if (pts.length === 1) return `M ${sx(pts[0])} ${sy(pts[0])}`;
  if (pts.length === 2) return `M ${sx(pts[0])} ${sy(pts[0])} L ${sx(pts[1])} ${sy(pts[1])}`;
  let d = `M ${sx(pts[0])} ${sy(pts[0])}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const curr = pts[i];
    const next = pts[i + 1];
    const mx = (sx(curr) + sx(next)) / 2;
    const my = (sy(curr) + sy(next)) / 2;
    d += ` Q ${sx(curr)} ${sy(curr)} ${mx} ${my}`;
  }
  const last = pts[pts.length - 1];
  d += ` T ${sx(last)} ${sy(last)}`;
  return d;
};

const BundleAssetMap = ({ packId, embedded = false, selectedId, onSelectId }) => {
  const { platform, config } = usePlatform();
  const accent = config.accent;
  const isAndroid = platform === "android";
  const bgTop = isAndroid ? "#DCE6F2" : "#F6EBD1";
  const bgBot = isAndroid ? "#CFDCEC" : "#EFE4CB";
  const gridStroke = isAndroid ? "#C8D4E4" : "#E8DFCF";
  const parcelFill = isAndroid ? "#CBD9EC" : "#DCE9D9";
  const waterFill = isAndroid ? "#B9CBE4" : "#CDE4E4";

  const visible = visibleAssetsFor(packId);
  const count = visible.length;
  const [hoverId, setHoverId] = useState(null);
  // Fallback internal state if parent doesn't control the selection.
  const [internalSelected, setInternalSelected] = useState(visible[0]?.id ?? null);
  const effectiveSelected = selectedId !== undefined ? selectedId : internalSelected;
  const selectPin = (id) => (onSelectId ? onSelectId(id) : setInternalSelected(id));

  const [pulse, setPulse] = useState(false);
  const containerRef = useRef(null);

  // Keep internal selection valid when the pack size shrinks (uncontrolled case)
  useEffect(() => {
    if (onSelectId) return; // parent handles it
    if (!internalSelected || !visible.some((v) => v.id === internalSelected)) {
      setInternalSelected(visible[0]?.id ?? null);
    }
  }, [count]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ring + auto-scroll when pack size changes
  const prevCount = useRef(count);
  useEffect(() => {
    if (prevCount.current === count) return;
    prevCount.current = count;
    const el = containerRef.current;
    if (el) {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const fullyVisible = r.top >= 60 && r.bottom <= vh - 20;
      if (!fullyVisible) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setPulse(true);
    const t = setTimeout(() => setPulse(false), 900);
    return () => clearTimeout(t);
  }, [count]);

  const outerClass = embedded
    ? "relative h-full w-full"
    : "relative mt-5 overflow-hidden rounded-2xl bg-tracksy-cream";
  const innerClass = embedded
    ? "relative h-full w-full"
    : "relative aspect-[5/4] w-full sm:aspect-[16/9]";

  return (
    <motion.div
      ref={containerRef}
      data-testid="bundle-asset-map"
      animate={embedded ? undefined : (pulse ? { boxShadow: `0 0 0 4px ${accent}55, 0 10px 24px rgba(0,0,0,0.06)` } : { boxShadow: "0 1px 2px rgba(0,0,0,0.05)" })}
      transition={{ duration: 0.45 }}
      className={outerClass}
    >
      <div className={innerClass}>
        {/* Stylised map backdrop */}
        <svg viewBox="0 0 160 90" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
          <defs>
            <linearGradient id="bam-bg" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0" stopColor={bgTop} />
              <stop offset="1" stopColor={bgBot} />
            </linearGradient>
          </defs>
          <rect width="160" height="90" fill="url(#bam-bg)" />
          {Array.from({ length: 10 }).map((_, i) => (
            <path key={`h-${i}`} d={`M0 ${i * 9} H160`} stroke={gridStroke} strokeWidth="0.25" />
          ))}
          {Array.from({ length: 18 }).map((_, i) => (
            <path key={`v-${i}`} d={`M${i * 9} 0 V90`} stroke={gridStroke} strokeWidth="0.25" />
          ))}
          <ellipse cx="30" cy="30" rx="10" ry="6" fill={parcelFill} opacity="0.7" />
          <ellipse cx="110" cy="20" rx="8" ry="4" fill={parcelFill} opacity="0.7" />
          <ellipse cx="78" cy="62" rx="12" ry="6" fill={waterFill} opacity="0.6" />
          <ellipse cx="140" cy="70" rx="7" ry="5" fill={parcelFill} opacity="0.7" />

          {visible.length > 1 && (
            <>
              {/* Soft glow underlay — adds depth to the route */}
              <motion.path
                key={`glow-${count}`}
                d={buildRoutePath(visible)}
                fill="none"
                stroke={accent}
                strokeWidth="2.2"
                strokeLinecap="round"
                opacity={0.18}
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
                style={{ filter: "blur(0.9px)" }}
              />
              {/* Crisp dashed route — single smooth cubic curve through every pin.
                  A gentle travelling-ants animation keeps the route feeling alive without being busy. */}
              <motion.path
                key={`route-${count}`}
                d={buildRoutePath(visible)}
                fill="none"
                stroke={accent}
                strokeWidth="0.6"
                strokeDasharray="2.4 1.6"
                strokeLinecap="round"
                opacity={0.85}
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1, strokeDashoffset: [0, -8] }}
                transition={{
                  pathLength: { duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.15 },
                  strokeDashoffset: { duration: 12, repeat: Infinity, ease: "linear" },
                }}
              />
            </>
          )}
        </svg>

        {/* Pins */}
        <AnimatePresence>
          {visible.map((a, i) => {
            const Icon = a.icon;
            const active = hoverId === a.id;
            const highlighted = effectiveSelected === a.id;
            return (
              // Outer wrapper owns the positioning so (x%, y%) lands exactly on the ICON CENTRE
              // — route lines on the SVG use the same (x, y) and therefore line up perfectly.
              <div
                key={a.id}
                data-testid={`bundle-map-pin-${a.id}`}
                data-highlighted={highlighted}
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${a.x}%`, top: `${a.y}%`, zIndex: highlighted ? 30 : active ? 20 : 10 }}
              >
                <motion.button
                  type="button"
                  onMouseEnter={() => setHoverId(a.id)}
                  onMouseLeave={() => setHoverId((v) => (v === a.id ? null : v))}
                  onFocus={() => setHoverId(a.id)}
                  onBlur={() => setHoverId((v) => (v === a.id ? null : v))}
                  onClick={() => selectPin(a.id)}
                  aria-label={`${a.label}: ${a.tip}`}
                  aria-pressed={highlighted}
                  initial={{ y: -22, opacity: 0, scale: 0.5 }}
                  animate={{ y: 0, opacity: 1, scale: 1 }}
                  exit={{ y: -14, opacity: 0, scale: 0.6 }}
                  transition={{ type: "spring", stiffness: 260, damping: 16, delay: 0.05 + i * 0.08 }}
                  className="relative flex items-center justify-center focus:outline-none"
                >
                  <div
                    className="relative flex h-7 w-7 items-center justify-center rounded-full text-white shadow-glow transition-transform duration-500 ease-out sm:h-8 sm:w-8"
                    style={{ background: accent, transform: highlighted ? "scale(1.22)" : active ? "scale(1.08)" : undefined }}
                  >
                    <Icon className="relative z-10 h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={2.4} />
                    {/* Two hollow concentric rings — fade IN and OUT every loop (keyframed opacity)
                        so each cycle begins invisible and ends invisible: no snap, no flicker. */}
                    {highlighted && (
                      <>
                        <motion.span
                          key="hpulse-1"
                          initial={{ opacity: 0, scale: 1 }}
                          animate={{ opacity: [0, 0.6, 0], scale: [1, 1.7, 2.6] }}
                          transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut", times: [0, 0.35, 1] }}
                          className="pointer-events-none absolute inset-0 rounded-full border"
                          style={{ borderColor: accent, borderWidth: 1.5 }}
                        />
                        <motion.span
                          key="hpulse-2"
                          initial={{ opacity: 0, scale: 1 }}
                          animate={{ opacity: [0, 0.6, 0], scale: [1, 1.7, 2.6] }}
                          transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut", times: [0, 0.35, 1], delay: 1.2 }}
                          className="pointer-events-none absolute inset-0 rounded-full border"
                          style={{ borderColor: accent, borderWidth: 1.5 }}
                        />
                      </>
                    )}
                  </div>

                  {/* Stem + label chip hang BELOW the icon (absolute) so they never push the
                      icon off its (x, y) anchor — route lines connect icon-centre to icon-centre. */}
                  <div className="pointer-events-none absolute left-1/2 top-full flex -translate-x-1/2 flex-col items-center">
                    <span className="mt-0.5 h-1.5 w-1.5 rotate-45" style={{ background: accent }} />
                    <span
                      className="mt-1 whitespace-nowrap rounded-full px-1.5 py-0.5 text-[9px] font-bold shadow-subtle backdrop-blur-sm sm:text-[10px]"
                      style={{
                        background: highlighted ? accent : "rgba(255,255,255,0.95)",
                        color: highlighted ? "#fff" : "#1A1A1A",
                      }}
                    >
                      {a.label}
                    </span>
                    <AnimatePresence>
                      {active && (
                        <motion.span
                          initial={{ opacity: 0, y: -4, scale: 0.9 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -4, scale: 0.95 }}
                          transition={{ duration: 0.18 }}
                          className="mt-1 w-max max-w-[160px] rounded-lg bg-tracksy-ink px-2 py-1 text-center text-[10px] font-semibold leading-tight text-white shadow-lift"
                        >
                          {a.tip}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.button>
              </div>
            );
          })}
        </AnimatePresence>

        <div className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-tracksy-ink shadow-subtle backdrop-blur">
          <span className="inline-flex h-1.5 w-1.5 animate-pulse rounded-full" style={{ background: accent }} />
          Tracking {count} asset{count > 1 ? "s" : ""}
        </div>
      </div>
    </motion.div>
  );
};

export default BundleAssetMap;
